'use client'
import * as React from 'react'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { LFS_URL } from '@repo/web-utils/env'

import { Link } from '@repo/web-ui/components/link'
import footerLinks from '@repo/web-ui/components/cf-footer/data.json'
import { USPrivacyFooterLinks } from '@repo/web-ui/components/cf-footer/us-privacy-links'
import { STUDIO_CF_URL } from '@web/utils/env'

type Languages = keyof typeof footerLinks
type Sections = keyof (typeof footerLinks)['en']

const normalizeFooterLinkUrl = (url: string) => {
  if (url.indexOf('http') === 0 || url.indexOf('https') === 0) {
    return url
  }

  return url.indexOf('/') === 0 ? url : `/${url}`
}

const excludedFooterLinkUrls = new Set([
  'pod/',
  'single-sales-license/',
  'commercial-use-guide/',
  'studio/templates/',
])

const popularLinks = [
  { title: 'AI Photo Editor', url: `${STUDIO_CF_URL}/ai-image-editor/lp/ai-photo-editor` },
  { title: 'AI Image Generator', url: `${STUDIO_CF_URL}/flow/lp/ai-image-generator/` },
  { title: 'Image Upscaler', url: `${STUDIO_CF_URL}/image-upscaler/lp/image-upscaler` },
  { title: 'Vectorizer AI', url: `${STUDIO_CF_URL}/vectorizer/lp/vectorizer-ai` },
  { title: 'Logo Maker', url: `${STUDIO_CF_URL}/logo-creator/lp/logo-maker` },
  { title: 'Sound Effects', url: `${STUDIO_CF_URL}/ai-sound-effect-generator/lp/sound-effects` },
  {
    title: 'AI Video Generators',
    url: `${STUDIO_CF_URL}/ai-video-generator/lp/ai-video-generators`,
  },
  {
    title: 'Remove Person Photo',
    url: `${STUDIO_CF_URL}/remove-people-from-background/lp/remove-person-photo`,
  },
]

const FooterHeading = ({ children }: { children: React.ReactNode }) => (
  <p className="text-16 m-[20px_0px_15px_0px] leading-[1.4]">{children}</p>
)

const FooterText = ({ children }: { children: React.ReactNode }) => (
  <p className="text-12 pb-4 leading-[1.6]">{children}</p>
)

const FooterLink = ({
  children,
  className,
  href,
  target,
}: React.PropsWithChildren<{ className: string; href: string; target?: '_blank' }>) => {
  return (
    <Link
      prefetch={false}
      className={tw(
        'text-12 text-[#5ad47b] hover:text-[#7ee09a] min-h-6 inline-flex items-center',
        className,
      )}
      href={normalizeFooterLinkUrl(href)}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {children}
    </Link>
  )
}

export function HomepageFooter() {
  const t = useTranslations('components.cf_footer')
  const locale = useLocale()
  const renderFooterLinks = (section: Sections) => {
    const lang = (locale || 'en') as Languages
    const links = footerLinks[lang][section].filter(link => !excludedFooterLinkUrls.has(link.url))

    return (
      <ul className="m-0 list-none p-0">
        {links.map(link => (
          <li key={`${link.url}-${link.title}-${section}`}>
            <FooterLink className="" href={link.url}>
              {link.title.replace('&amp;', '&')}
            </FooterLink>
          </li>
        ))}
        {section === 'footer-links' ? <USPrivacyFooterLinks /> : null}
      </ul>
    )
  }

  return (
    <footer className="text-cf-white whitespace-pre-line bg-[#393c57] p-1 md:p-[20px_0px_16px_0px]">
      <div className="mx-auto w-full px-5 md:max-w-[1220px]">
        <div className="ml-[-16px] mt-[-16px] flex w-[calc(100%+16px)] flex-wrap">
          <div className="w-full flex-grow-0 basis-[100%] pl-4 pt-4 md:max-w-[50%] md:basis-1/2 lg:max-w-[25%] lg:basis-1/4">
            <div>
              <FooterHeading>Creative Fabrica Studio</FooterHeading>
              <FooterText>{t('homepage_description')}</FooterText>
              <FooterLink className="mb-3 block" href={AppRoutes.ABOUT}>
                {t('learn_more')}
              </FooterLink>
              <FooterLink className="mb-3 block" href={AppRoutes.HIRING}>
                {t('hiring')}
              </FooterLink>
              <FooterLink className="mb-3 block" href={AppRoutes.THE_ARTISTRY}>
                {t('blog')}
              </FooterLink>
            </div>
            <div className="mt-8" data-testid="footer-links">
              <FooterHeading>{t('links')}</FooterHeading>
              {renderFooterLinks('footer-links')}
            </div>
          </div>

          <div className="w-full flex-grow-0 basis-[100%] pl-4 pt-4 md:max-w-[50%] md:basis-1/2 lg:max-w-[25%] lg:basis-1/4">
            <div data-testid="footer-popular">
              <FooterHeading>{t('popular_tools')}</FooterHeading>
              <ul className="m-0 list-none p-0">
                {popularLinks.map(link => (
                  <li key={`${link.url}-${link.title}`}>
                    <FooterLink className="" href={link.url} target="_blank">
                      {link.title}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="w-full flex-grow-0 basis-[100%] pl-4 pt-4 md:max-w-[50%] md:basis-1/2 lg:max-w-[25%] lg:basis-1/4">
            <FooterHeading>{t('contact')}</FooterHeading>
            <FooterText>{t('contact_us')}</FooterText>
            <FooterText>{t('address')}</FooterText>
            <FooterText>{t('chamber_of_commerce')}</FooterText>

            <div data-testid="footer-tools">
              <FooterHeading>{t('tools')}</FooterHeading>
              {renderFooterLinks('footer-tools')}
            </div>
          </div>

          <div className="w-full flex-grow-0 basis-[100%] pl-4 pt-4 md:max-w-[50%] md:basis-1/2 lg:max-w-[25%] lg:basis-1/4">
            <div className="pr-0 lg:pr-8">
              <FooterHeading>{t('secure_checkout')}</FooterHeading>
              <FooterText>{t('your_data')}</FooterText>
              <Image
                src={`${LFS_URL}/web/pages/studio/footer/powered-by-stripe.png`}
                alt="Stripe"
                width={150}
                height={33}
                className="!justify-start"
              />
              <div className="mb-2" />
              <Image
                src={`${LFS_URL}/web/pages/studio/footer/pp-secure-checkout.png`}
                alt="Paypal"
                width={150}
                height={60}
                className="!justify-start"
              />
            </div>
          </div>

          <div className="w-full flex-grow-0 basis-[100%] pl-4 pt-4">
            <div className="border-1 mt-6 flex flex-col justify-between border-t border-[rgba(255,255,255,.2)] pt-7 md:flex-row md:gap-4">
              <FooterText>
                <span suppressHydrationWarning={true}>
                  {t('copy', {
                    currentYear: new Date().getFullYear(),
                  })}
                </span>
              </FooterText>
              <div className="text-12">
                {t('questions_mail_us')}
                <FooterLink className="mb-3 ml-1" href="mailto:hi@creativefabrica.com">
                  hi@creativefabrica.com
                </FooterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
